import { useMemo } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { RiDraggable } from '@/shared/icons/remix'
import { resolveRankingOrder, type RankingAnswer } from '../../../lib/ranking-order'
import { resolveSurveyMediaUrl } from '../../../lib/resolve-survey-media-url'
import type { SurveyOption, SurveyQuestion } from '../../../lib/survey-question.types'
import {
  surveyRankingBadgeSx,
  surveyRankingDragHandleSx,
  surveyRankingHintSx,
  surveyRankingInvalidSx,
  surveyRankingLabelSx,
  surveyRankingListSx,
  surveyRankingRootSx,
  surveyRankingRowSx,
  surveyRankingThumbSx,
} from '../../../styles/surveyExecutionStyles'

export const SURVEY_RANKING_DRAG_HINT =
  'Drag items to rank from most to least important'

interface RankingQuestionProps {
  readonly question: SurveyQuestion
  readonly value: RankingAnswer | null
  readonly onChange: (value: RankingAnswer) => void
}

function rankingThumbPx(): number {
  /** Matches Sensemaker ranking live preview (fixed 38px thumb). */
  return 38
}

function SortableRankingRow({
  id,
  rank,
  option,
  thumbPx,
  questionId,
}: Readonly<{
  id: string
  rank: number
  option: SurveyOption
  thumbPx: number
  questionId: string
}>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.92 : 1,
  } as const

  const imageSrc = resolveSurveyMediaUrl(option.imageUrl)
  const hasImage = Boolean(imageSrc)

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={surveyRankingRowSx}
      data-testid={`survey-q-${questionId}-rank-row-${id}`}
    >
      <Box
        component="button"
        type="button"
        aria-label={`Drag to reorder rank ${rank}`}
        data-testid={`survey-q-${questionId}-rank-handle-${id}`}
        sx={surveyRankingDragHandleSx}
        {...attributes}
        {...listeners}
      >
        <RiDraggable size={16} aria-hidden />
      </Box>
      <Box sx={surveyRankingBadgeSx} aria-hidden>
        {rank}
      </Box>
      <Typography component="span" sx={surveyRankingLabelSx}>
        {option.label}
      </Typography>
      {hasImage ? (
        <Box
          component="img"
          src={imageSrc ?? undefined}
          alt=""
          sx={{ ...surveyRankingThumbSx, width: thumbPx, height: thumbPx }}
        />
      ) : null}
    </Box>
  )
}

export function RankingQuestion({ question, value, onChange }: RankingQuestionProps) {
  const optionIds = useMemo(
    () => question.options.map((o) => o.id),
    [question.options],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const order = resolveRankingOrder(value, optionIds)
  const optionById = useMemo(() => {
    const map = new Map<string, SurveyOption>()
    for (const opt of question.options) map.set(opt.id, opt)
    return map
  }, [question.options])

  if (optionIds.length < 2) {
    return (
      <Typography sx={surveyRankingInvalidSx} data-testid={`survey-q-${question.id}-ranking-invalid`}>
        This ranking question needs at least two options.
      </Typography>
    )
  }

  const thumbPx = rankingThumbPx()

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return
    const oldIndex = order.indexOf(String(active.id))
    const newIndex = order.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    if (active.id === over.id) {
      onChange([...order])
      return
    }
    onChange(arrayMove(order, oldIndex, newIndex))
  }

  return (
    <Box sx={surveyRankingRootSx} data-testid={`survey-q-${question.id}-ranking`}>
      <Typography component="p" sx={surveyRankingHintSx} data-testid={`survey-q-${question.id}-ranking-hint`}>
        {SURVEY_RANKING_DRAG_HINT}
      </Typography>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <Box sx={surveyRankingListSx} role="list" aria-label={question.prompt}>
            {order.map((id, index) => {
              const option = optionById.get(id)
              if (!option) return null
              return (
                <SortableRankingRow
                  key={id}
                  id={id}
                  rank={index + 1}
                  option={option}
                  thumbPx={thumbPx}
                  questionId={question.id}
                />
              )
            })}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  )
}
